import React, { useState, useCallback, useMemo } from 'react'

import ExploitCard from './components/ExploitCard'
import GlitchText from './components/GlitchText'
import IPStatusPanel from './components/IPStatusPanel'
import MatrixRain from './components/MatrixRain'
import MouseoverExploit from './components/MouseoverExploit'
import ResultDialog from './components/ResultDialog'
import ScanlineOverlay from './components/ScanlineOverlay'
import SecurityToggles from './components/SecurityToggles'
import SkullWarning from './components/SkullWarning'
import useExploitRunner from './hooks/useExploitRunner'
import useExtensionDetection from './hooks/useExtensionDetection'
import styles from './index.module.css'
import {
  EXPLOIT_CONFIGS,
  EXTENSION_ID,
  getCategoryLabel,
  type ExploitConfig,
  type ExploitType,
} from './utils/exploitPayloads'

const ExploitDemo: React.FC = () => {
  const {
    isRunning,
    lastExploit,
    runExploit,
  } = useExploitRunner()

  const { isInstalled, isChecking } = useExtensionDetection()

  const [isShaking, setIsShaking] = useState(false)
  const [matrixBurst, setMatrixBurst] = useState(false)
  const [showFlicker, setShowFlicker] = useState(false)
  const [showVHS, setShowVHS] = useState(false)
  const [showSkull, setShowSkull] = useState(false)
  const [splashText, setSplashText] = useState<{ text: string; danger: boolean } | null>(null)
  const [dialogData, setDialogData] = useState<{
    type: 'vpnState' | 'extensionList' | 'bypassList'
    data: unknown
  } | null>(null)

  const triggerEffects = useCallback((severity: ExploitConfig['severity']) => {
    setMatrixBurst(true)
    setTimeout(() => setMatrixBurst(false), 1000)

    if (severity === 'critical') {
      setIsShaking(true)
      setShowFlicker(true)
      setShowVHS(true)
      setShowSkull(true)
      setSplashText({ text: 'BREACH DETECTED', danger: true })

      setTimeout(() => setIsShaking(false), 300)
      setTimeout(() => setShowFlicker(false), 500)
      setTimeout(() => setShowVHS(false), 800)
      setTimeout(() => setShowSkull(false), 3000)
      setTimeout(() => setSplashText(null), 2000)
    } else if (severity === 'high') {
      setIsShaking(true)
      setShowFlicker(true)
      setSplashText({ text: 'EXPLOIT SENT', danger: false })

      setTimeout(() => setIsShaking(false), 200)
      setTimeout(() => setShowFlicker(false), 300)
      setTimeout(() => setSplashText(null), 1500)
    }
  }, [])

  const handleExploit = useCallback(
    async (config: ExploitConfig, input?: string) => {
      triggerEffects(config.severity)
      const result = await runExploit(config.id as ExploitType, input)

      // Show dialog for async exploits that return data
      if (result.success && result.data) {
        if (config.id === 'getVPNState') {
          setDialogData({ type: 'vpnState', data: result.data })
        } else if (config.id === 'getExtensionList') {
          setDialogData({ type: 'extensionList', data: result.data })
        } else if (config.id === 'getBypassList') {
          setDialogData({ type: 'bypassList', data: result.data })
        }
      }
    },
    [runExploit, triggerEffects]
  )

  const closeDialog = useCallback(() => {
    setDialogData(null)
  }, [])

  const handleIPChange = useCallback(
    (_oldIP: string, _newIP: string) => {
      setIsShaking(true)
      setShowFlicker(true)
      setSplashText({ text: 'IP EXPOSED!', danger: true })

      setTimeout(() => setIsShaking(false), 500)
      setTimeout(() => setShowFlicker(false), 800)
      setTimeout(() => setSplashText(null), 2500)
    },
    []
  )

  const handleTabBomb = useCallback(() => {
    triggerEffects('medium')
    runExploit('tabBomb', '10')
  }, [runExploit, triggerEffects])

  const exploitsByCategory = useMemo(() => {
    const categories = new Map<ExploitConfig['category'], ExploitConfig[]>()

    EXPLOIT_CONFIGS.forEach((config) => {
      const existing = categories.get(config.category) || []
      categories.set(config.category, [...existing, config])
    })

    return categories
  }, [])

  const categoryOrder: ExploitConfig['category'][] = [
    'vpn',
    'extension',
    'security',
  ]

  const exploitsDisabled = isChecking || !isInstalled

  return (
    <div className={`${styles.pageContainer} ${isShaking ? styles.shake : ''}`}>
      <MatrixRain burst={matrixBurst} />
      <ScanlineOverlay flicker={showFlicker} />

      <div className={`${styles.vhsDistortion} ${showVHS ? styles.active : ''}`} />

      {splashText && (
        <div className={`${styles.accessSplash} ${styles.visible}`}>
          <div className={`${styles.splashText} ${splashText.danger ? styles.danger : ''}`}>
            {splashText.text}
          </div>
        </div>
      )}

      {dialogData && (
        <ResultDialog
          type={dialogData.type}
          data={dialogData.data}
          onClose={closeDialog}
        />
      )}

      <div className={styles.content}>
        <header className={styles.header}>
          <GlitchText className={styles.title}>
            URBAN VPN EXPLOIT POC
          </GlitchText>
          <p className={styles.subtitle}>
            Urban VPN (24M+ users) contains a critical postMessage vulnerability allowing any website
            to execute privileged commands. This PoC demonstrates remote VPN disconnection leading to
            IP exposure.
          </p>
          <div className={styles.extensionId}>
            Extension ID: {EXTENSION_ID}
          </div>
        </header>

        <div className={styles.mainGrid}>
          <section className={styles.ipPanelSection}>
            <h2 className={styles.sectionTitle}>
              <span className={styles.sectionIcon}>🎯</span>
              Real-Time IP Exposure Monitor
            </h2>
            <IPStatusPanel onIPChange={handleIPChange} />
          </section>

          <section className={styles.exploitsSection}>
            <h2 className={styles.sectionTitle}>
              <span className={styles.sectionIcon}>⚡</span>
              Available Exploits
            </h2>

            <div className={styles.warningBox}>
              <strong>Critical Vulnerability: postMessage Command Injection</strong>
              <ul className={styles.bulletList}>
                <li>Extension validates messages using only the <strong>public extension ID</strong> and a hardcoded string ("Toad") — both trivially spoofable</li>
                <li>Any webpage can <strong>disconnect your VPN</strong>, exposing your real IP address</li>
                <li>Any webpage can <strong>force VPN to connect through Russia or China</strong></li>
                <li>Any webpage can <strong>disable security features</strong>: anti-malware, anti-malicious ads, WebRTC leak protection</li>
                <li>Any webpage can <strong>opt users back into data collection</strong> after they've opted out</li>
                <li>Any webpage can <strong>disable competing security extensions</strong> (other VPNs, ad blockers)</li>
                <li>Any webpage can <strong>add domains to the VPN bypass list</strong>, selectively exposing traffic</li>
                <li>Any webpage can <strong>open unlimited tabs without user interaction</strong> for phishing attacks</li>
              </ul>
            </div>

            {!isChecking && !isInstalled && (
              <div className={styles.notInstalledBox}>
                <strong>Extension Not Detected</strong>
                <p>
                  Urban VPN extension is not installed or not accessible. Install the extension on
                  a fresh Chrome profile or a burner PC to test these exploits safely.
                </p>
                <div className={styles.trackingWarning}>
                  <strong>⚠️ Data Harvesting Warning</strong>
                  <ul className={styles.bulletList}>
                    <li>Urban VPN injects scripts into Facebook, Instagram, Twitter, LinkedIn, Reddit, TikTok, Pinterest, and Twitch</li>
                    <li>Collected data includes video URLs, ad metadata, user activity, and browsing patterns</li>
                    <li>Data is exfiltrated to Urban VPN servers (api-pro.urban-vpn.com, stats.urban-vpn.com)</li>
                  </ul>
                </div>
              </div>
            )}

            {showSkull && (
              <SkullWarning
                message={`CRITICAL: ${lastExploit?.toUpperCase() || 'EXPLOIT'} EXECUTED`}
                visible={showSkull}
              />
            )}

            {categoryOrder.map((category) => {
              const configs = exploitsByCategory.get(category)
              if (!configs) return null

              // Filter out the toggle-based exploits from security category
              const filteredConfigs = category === 'security'
                ? configs.filter(c => ![
                    'enableWebRTC', 'disableWebRTC',
                    'enableAntiMalware', 'disableAntiMalware',
                    'enableAntiPhishing', 'disableAntiPhishing',
                    'enableAdBlocker', 'disableAdBlocker',
                    'enableDataCollection', 'disableDataCollection',
                  ].includes(c.id))
                : configs

              return (
                <div key={category} className={styles.categorySection}>
                  <h3 className={styles.categoryTitle}>{getCategoryLabel(category)}</h3>

                  {category === 'security' && (
                    <div style={{ marginBottom: '16px' }}>
                      <SecurityToggles disabled={exploitsDisabled} />
                    </div>
                  )}

                  <div className={styles.exploitGrid}>
                    {filteredConfigs.map((config) => (
                      <ExploitCard
                        key={config.id}
                        config={config}
                        index={EXPLOIT_CONFIGS.findIndex((c) => c.id === config.id)}
                        onExecute={(input) => handleExploit(config, input)}
                        isRunning={isRunning && lastExploit === config.id}
                        disabled={exploitsDisabled}
                      />
                    ))}
                    {category === 'vpn' && (
                      <MouseoverExploit
                        title="Tab Bomb (Hover Over Me)"
                        description="Hover for 3 seconds to open 10 tabs — no click required"
                        onTrigger={handleTabBomb}
                        disabled={exploitsDisabled}
                        duration={3000}
                      />
                    )}
                  </div>
                </div>
              )
            })}
          </section>
        </div>

        <footer className={styles.footer}>
          <p className={styles.footerText}>
            Urban VPN Vulnerability Demonstration • For Security Research Only
          </p>
        </footer>
      </div>
    </div>
  )
}

export default ExploitDemo
